import { useState } from "react";
import { toast } from "sonner";
import api, { apiError, brl } from "@/lib/api";
import { Modal } from "@/components/panel/ui";
import { Button, Field, Input } from "@/components/ui-kit";
import { PauseCircle, Trash2, ShoppingCart, AlertTriangle } from "lucide-react";

/** "há 5 min" — o que importa na lista é a espera, não o relógio. */
function desde(iso) {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h}h`;

  return `há ${Math.round(h / 24)}d`;
}

/**
 * Diálogo de guardar: pede só um apelido.
 *
 * Uma lista de "PDV em aberto #1, #2, #3" é inútil para quem tem três clientes
 * circulando pela loja. O vendedor identifica pela pessoa ("moça da cadeira
 * azul"), então é isso que o campo pede. Continua opcional: obrigar a digitar
 * anularia o ganho de liberar o balcão rápido.
 */
export function SaveHeldSaleModal({ open, onClose, onSave, defaultLabel, busy }) {
  const [label, setLabel] = useState("");

  return (
    <Modal open={open} onClose={onClose} title="Guardar venda em aberto" testId="pos-hold-save">
      <p className="text-sm text-slate-600">
        A venda fica guardada esperando o cliente decidir. <b>Nada é cobrado e o estoque não é reservado</b> —
        as peças seguem disponíveis para quem fechar antes.
      </p>
      <Field label="Como identificar este cliente?" hint="Opcional — ajuda a achar na lista depois." className="mt-4">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} autoFocus
          placeholder={defaultLabel || "Ex.: moça da cadeira azul"} data-testid="pos-hold-label"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSave(label.trim() || defaultLabel || ""); } }} />
      </Field>
      <div className="flex gap-3 mt-5">
        <Button variant="secondary" block onClick={onClose}>Voltar</Button>
        <Button block loading={busy} onClick={() => onSave(label.trim() || defaultLabel || "")}
          data-testid="pos-hold-save-confirm">Guardar</Button>
      </div>
    </Modal>
  );
}

/**
 * Lista das vendas em aberto, para retomar ou descartar.
 *
 * Retomar TROCA o carrinho atual. O aviso disso fica no próprio botão do PDV
 * (que só aparece quando há algo a perder), não aqui — um segundo diálogo de
 * confirmação no meio da fila do balcão custa mais do que protege.
 */
export function HeldSalesModal({ open, onClose, items, onResume, onDiscard, loadingId }) {
  return (
    <Modal open={open} onClose={onClose} title="Vendas em aberto" maxWidth="max-w-lg" testId="pos-held-list">
      {items.length === 0 ? (
        <div className="text-center py-8">
          <PauseCircle size={28} className="mx-auto text-slate-300 mb-3" aria-hidden />
          <p className="text-sm font-medium text-slate-600">Nenhuma venda em aberto</p>
          <p className="text-xs text-slate-400 mt-1">
            Monte o carrinho e use <b>Guardar</b> para liberar o balcão sem perder a venda.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 -mx-2" data-testid="pos-held-items">
          {items.map((h) => (
            <li key={h.id} className="flex items-center gap-3 px-2 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {h.label || h.customer_name || "Venda sem identificação"}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {h.qty} {h.qty === 1 ? "item" : "itens"} · <span className="font-mono">{brl(h.subtotal)}</span> · {desde(h.updated_at)}
                  {h.customer_name && h.label ? ` · ${h.customer_name}` : ""}
                </p>
              </div>
              <Button size="sm" icon={ShoppingCart} onClick={() => onResume(h)} loading={loadingId === h.id}
                data-testid="pos-held-resume">Retomar</Button>
              <Button variant="ghost" size="iconSm" onClick={() => onDiscard(h)} aria-label={`Descartar ${h.label || "venda"}`}
                className="hover:text-red-600" data-testid="pos-held-discard"><Trash2 size={15} /></Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * O que mudou no mundo entre guardar e retomar.
 *
 * Preço reajustado e peça vendida para outro cliente são os dois casos que
 * acontecem de verdade, e os dois fazem o vendedor prometer errado se ninguém
 * avisar. O carrinho é montado com os valores de AGORA; este aviso existe para
 * ele saber o que renegociar antes de falar com o cliente.
 *
 * O título é parâmetro porque o mesmo aviso serve à venda guardada e à compra
 * anterior repetida — e "desde que você guardou" só é verdade num dos dois.
 */
export function ResumeWarnings({ items, title = "Mudou desde que você guardou" }) {
  const semEstoque = items.filter((i) => i.unavailable);
  const mudouPreco = items.filter((i) => i.price_changed && !i.unavailable);
  if (!semEstoque.length && !mudouPreco.length) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 space-y-1"
      data-testid="pos-resume-warnings">
      <p className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle size={13} aria-hidden /> {title}
      </p>
      {semEstoque.map((i) => (
        <p key={`s-${i.variant_id}`}>
          <b>{i.name}</b> {i.variant_label ? `(${i.variant_label})` : ""} — {i.stock > 0 ? `só ${i.stock} em estoque` : "sem estoque"}
        </p>
      ))}
      {mudouPreco.map((i) => (
        <p key={`p-${i.variant_id}`}>
          <b>{i.name}</b> — preço de {brl(i.held_price)} para <b>{brl(i.price)}</b>
        </p>
      ))}
    </div>
  );
}

/** Carrega a lista de vendas em aberto do vendedor. */
export async function fetchHeldSales() {
  try {
    const { data } = await api.get("/pos/held-sales");
    return Array.isArray(data) ? data : [];
  } catch {
    return [];   // a lista é auxiliar: falhar aqui não pode travar a venda
  }
}

export async function discardHeldSale(id) {
  try {
    await api.delete(`/pos/held-sales/${id}`);
    return true;
  } catch (e) {
    toast.error(apiError(e));
    return false;
  }
}
